import ImageIcon from "@mui/icons-material/Image";
import clsx from "clsx";
import PropTypes from "prop-types";
import { createRef, useCallback, useEffect, useRef } from "react";

import { LeakSourceIndicator } from "./LeakSourceIndicator";

export const imageFrameTypes = Object.freeze({
  scanSwir: "jpg",
  scanRgb: "scanrgb",
  swir: "img",
  rgb: "rgb"
});

const MAX_IMAGES_PRELOAD = 5;

export const ImageFrames = ({
  type = imageFrameTypes.swir,
  classes,
  sortedFrames,
  leakSourceCoordinates = [],
  imageErrors,
  frameIndex,
  setImageErrors,
  handleImageError,
  showLeakIndicator = false,
  playbackDirection
}) => {
  const preloadedImages = useRef([]);
  const imageRefs = sortedFrames?.map(() => createRef());
  const onImageLoad = idx => () =>
    setImageErrors(prevState => ({ ...prevState, [idx]: false }));

  const preloadImages = useCallback(
    urls => {
      const loadPromises = urls.map((url, index) => {
        return new Promise((resolve, reject) => {
          let imageIndex = frameIndex + index * playbackDirection;

          if (imageIndex <= 0) imageIndex += sortedFrames.length;
          if (imageIndex >= sortedFrames.length)
            imageIndex -= sortedFrames.length;

          if (!preloadedImages.current[imageIndex]) {
            const img = new Image();
            img.onload = resolve;
            img.onerror = reject;
            img.src = url;
            preloadedImages.current[imageIndex] = img;
          } else {
            resolve();
          }
        });
      });

      return Promise.all(loadPromises);
    },
    [frameIndex, playbackDirection, sortedFrames.length]
  );

  useEffect(() => {
    preloadedImages.current = [];
  }, [type, sortedFrames]);

  useEffect(() => {
    let chunkOfFrames;
    if (playbackDirection === -1) {
      const start = frameIndex - MAX_IMAGES_PRELOAD + 1;
      const end = frameIndex + 1;
      if (start < 0) {
        chunkOfFrames = [
          ...sortedFrames.slice(start + sortedFrames.length),
          ...sortedFrames.slice(0, end)
        ]
          .reverse()
          .map(frame => frame[type]);
      } else {
        chunkOfFrames = sortedFrames
          .slice(start, end)
          .reverse()
          .map(frame => frame[type]);
      }
    } else {
      chunkOfFrames = sortedFrames
        .slice(frameIndex, frameIndex + MAX_IMAGES_PRELOAD)
        .map(frame => frame[type]);
    }

    preloadImages(chunkOfFrames).catch(error => console.error(error));
  }, [frameIndex, sortedFrames, type, preloadImages, playbackDirection]);

  return (
    <div className={classes.frame} data-testid={type}>
      {preloadedImages.current.map((img, index) => {
        const frame = sortedFrames[index];
        const key = `${frame?.scresId ?? frame?.id ?? index}-${type}`;
        const hasError = imageErrors[index];
        const src = img?.src;
        const frameIndexInRefs = sortedFrames?.findIndex(
          fr => fr?.id === frame?.id
        );
        const imageRef = imageRefs[frameIndexInRefs];

        return hasError ? (
          <div
            key={key}
            className={clsx(classes.absoluteFull, classes.imageError)}
          >
            <ImageIcon />
            <span>Not found</span>
          </div>
        ) : (
          <div
            key={`leak-source-indicator-${key}`}
            className={classes.absoluteFull}
          >
            <img
              ref={imageRef}
              src={src}
              alt={`Alarm ${type} frame ${index}`}
              className={classes.image}
              style={{
                opacity: index === frameIndex ? 1 : 0
              }}
              onLoad={onImageLoad(index)}
              onError={handleImageError(index)}
            />

            <LeakSourceIndicator
              img={imageRef}
              leakSourceCoordinates={leakSourceCoordinates}
              isVisible={showLeakIndicator && index === frameIndex}
            />
          </div>
        );
      })}
    </div>
  );
};

ImageFrames.propTypes = {
  type: PropTypes.oneOf(Object.values(imageFrameTypes)),
  classes: PropTypes.object,
  sortedFrames: PropTypes.arrayOf(PropTypes.object),
  leakSourceCoordinates: PropTypes.array,
  imageErrors: PropTypes.object,
  frameIndex: PropTypes.number,
  setImageErrors: PropTypes.func,
  handleImageError: PropTypes.func,
  showLeakIndicator: PropTypes.bool,
  playbackDirection: PropTypes.number
};
